import type { TextRun } from "@bezpart-board/shared";
import { htmlToRuns, runsToHtml } from "./textRuns";

/**
 * Поле набора надписи как модель: текст с оформлением плюс место курсора.
 *
 * Править строки прямо в разметке поля опасно: браузер держит переносы то
 * как <br>, то как блоки, и маркер списка легко вставить «не туда». Поэтому
 * для правок списка поле читается в модель (куски + позиция курсора в
 * обычном тексте), правка считается чистой функцией, и поле собирается
 * заново из тех же кусков — оформление внутри строки не теряется.
 */

/** Символы-метки места курсора. Из частной области Юникода — в тексте их нет. */
const CARET_START = "";
const CARET_END = "";

export type EditorModel = {
  runs: TextRun[];
  /** Начало и конец выделения в обычном тексте; без выделения равны. */
  start: number;
  end: number;
};

/** Прочитать поле вместе с курсором. null — курсора в поле нет. */
export function readEditorModel(root: HTMLElement): EditorModel | null {
  const sel = root.ownerDocument.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;

  // Метки вставляем во временную копию: само поле и его историю правок не трогаем.
  const startMark = root.ownerDocument.createTextNode(CARET_START);
  const endMark = root.ownerDocument.createTextNode(CARET_END);
  const endRange = range.cloneRange();
  endRange.collapse(false);
  endRange.insertNode(endMark);
  const startRange = range.cloneRange();
  startRange.collapse(true);
  startRange.insertNode(startMark);

  const marked = htmlToRuns(root);

  // Метки убрали — поле ровно такое, каким было.
  startMark.remove();
  endMark.remove();
  root.normalize();

  let start = -1;
  let end = -1;
  let pos = 0;
  const runs: TextRun[] = [];
  for (const run of marked) {
    let text = "";
    for (const ch of run.text) {
      if (ch === CARET_START) start = pos;
      else if (ch === CARET_END) end = pos;
      else {
        text += ch;
        pos += ch.length;
      }
    }
    if (text) runs.push({ ...run, text });
  }
  if (start < 0) return null;
  if (end < 0) end = start;
  return { runs, start: Math.min(start, end), end: Math.max(start, end) };
}

/**
 * Собрать поле из кусков и поставить курсор на позицию обычного текста.
 * С selEnd — выделить [caret, selEnd): после маркера кусок остаётся выделенным.
 */
export function writeEditorModel(
  root: HTMLElement,
  runs: TextRun[],
  caret: number,
  selEnd?: number
): void {
  const text = runs.map((r) => r.text).join("");
  // Последняя пустая строка без «хвостового» <br> браузер не показывает:
  // после Enter в конце надписи курсору негде было бы стоять.
  const baseAttr = Number(root.dataset.wbBaseFont);
  const base = Number.isFinite(baseAttr) && baseAttr > 0 ? baseAttr : undefined;
  root.innerHTML = runsToHtml(runs, base) + (text.endsWith("\n") || !text ? '<br data-wb-tail="1">' : "");
  placeCaret(root, caret, selEnd);
}

/** Поставить курсор (или выделение до end) на позицию обычного текста. */
export function placeCaret(root: HTMLElement, offset: number, end?: number): void {
  const doc = root.ownerDocument;
  const sel = doc.getSelection();
  if (!sel) return;
  const range = doc.createRange();
  setBoundary(root, range, offset, true);
  if (typeof end === "number" && end > offset) setBoundary(root, range, end, false);
  else range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Поставить начало или конец диапазона на позицию обычного текста. */
function setBoundary(root: HTMLElement, range: Range, offset: number, isStart: boolean): void {
  let remaining = Math.max(0, offset);
  const at = (node: Node, off: number) =>
    isStart ? range.setStart(node, off) : range.setEnd(node, off);
  const before = (node: Node) => (isStart ? range.setStartBefore(node) : range.setEndBefore(node));
  const after = (node: Node) => (isStart ? range.setStartAfter(node) : range.setEndAfter(node));

  const walk = (node: Node): boolean => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const len = child.textContent?.length ?? 0;
        if (remaining <= len) {
          at(child, remaining);
          return true;
        }
        remaining -= len;
        continue;
      }
      if (!(child instanceof HTMLElement)) continue;
      if (child.tagName === "BR") {
        if (child.hasAttribute("data-wb-tail")) continue;
        if (remaining === 0) {
          before(child);
          return true;
        }
        remaining -= 1;
        if (remaining === 0) {
          after(child);
          return true;
        }
        continue;
      }
      if (walk(child)) return true;
    }
    return false;
  };

  if (!walk(root)) {
    // Позиция за концом текста — в самый конец.
    const tail = root.querySelector("br[data-wb-tail]");
    if (tail) before(tail);
    else at(root, root.childNodes.length);
  }
}
